import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FiEdit3 } from "react-icons/fi";
import { MdOutlineDelete } from "react-icons/md";
import { AiOutlineEye } from "react-icons/ai";

import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  ExternalContainer,
  InternalContainer,
} from "../../components/Reusable/Layout";
import { GradientButton, Search } from "../../components/forms";
import { fetchSupport, deleteSupport } from "../../features/auth/store/supportSlice";
import { SupportItem } from "../../features/auth/store/types/support-types";
import SupportManagementModal from "./Modal/SupportManagementModal";
import DeleteSupportModal from "./Modal/DeleteSupportModal";
import ViewSupportModal from "./Modal/ViewSupportModal";

const Support = () => {
  const dispatch = useAppDispatch();
  const items = useAppSelector((state: any) => state.support?.items ?? []);
  const loading = useAppSelector((state: any) => state.support?.loading ?? false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<SupportItem | null>(null);
  const [search, setSearch] = useState("");

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<SupportItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingItem, setViewingItem] = useState<SupportItem | null>(null);

  useEffect(() => {
    dispatch(fetchSupport());
  }, [dispatch]);

  const filteredItems = items.filter((item: SupportItem) =>
    [item.question, item.answer]
      .join(" ")
      .toLowerCase()
      .includes(search.trim().toLowerCase())
  );

  const openDeleteModal = (item: SupportItem) => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      await dispatch(deleteSupport(itemToDelete.id)).unwrap();
      toast.success("Support item deleted successfully");
      setDeleteModalOpen(false);
      setItemToDelete(null);
    } catch (err: any) {
      toast.error(err || "Failed to delete support item");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <ExternalContainer>
      <div className="h-screen w-[1030px]">
        <InternalContainer headerText="Support Management">
          <div className="flex flex-col gap-4">
            <div className="row-4">
              <Search
                placeholder="Search support items..."
                onChange={(e) => setSearch(e.target.value)}
              />
              <GradientButton
                content="Manage Support"
                onClick={() => { setEditingItem(null); setIsModalOpen(true); }}
              />
            </div>

            <div className="flex flex-col">
              <div className="overflow-x-auto">
                <table className="w-full border border-[#FFFFFF1A] rounded-2xl overflow-hidden font-Inter cursor-pointer">
                  <thead>
                    <tr className="bg-gray-100 dark:bg-[#FFFFFF1A] text-gray-600 dark:text-white uppercase text-xs bp:text-sm leading-normal h-[50px]">
                      <th className="px-6 text-left">#</th>
                      <th className="px-6 text-left">Question</th>
                      <th className="px-6 text-left">Answer</th>
                      <th className="px-6 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600 dark:text-white text-xs bp:text-sm">
                    {loading ? (
                      <tr className="border-b border-gray-200 h-[80px]">
                        <td colSpan={4} className="px-6 text-center">
                          <div className="flex items-center justify-center h-full">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                          </div>
                        </td>
                      </tr>
                    ) : filteredItems.length > 0 ? (
                      filteredItems.map((item: SupportItem, index: number) => (
                        <tr
                          key={item.id}
                          className="border-b border-gray-200 h-[50px]"
                        >
                          <td className="px-6 py-4 align-middle text-gray-400">
                            {index + 1}
                          </td>
                          <td className="px-6 py-4 align-middle font-medium max-w-[220px]">
                            {item.question}
                          </td>
                          <td className="px-6 py-4 align-middle max-w-[340px]">
                            <div
                              className="line-clamp-2 text-gray-500 dark:text-gray-400 prose prose-sm max-w-none dark:prose-invert"
                              dangerouslySetInnerHTML={{ __html: item.answer }}
                            />
                          </td>
                          <td className="px-6 py-4 align-middle">
                            <div className="flex items-center gap-3">
                              <button
                                className="text-green-600"
                                onClick={() => { setViewingItem(item); setViewModalOpen(true); }}
                              >
                                <AiOutlineEye className="text-2xl" />
                              </button>
                              <button
                                className="text-blue-600"
                                onClick={() => { setEditingItem(item); setIsModalOpen(true); }}
                              >
                                <FiEdit3 className="text-2xl" />
                              </button>
                              <button
                                className="text-red-600"
                                onClick={() => openDeleteModal(item)}
                              >
                                <MdOutlineDelete className="text-2xl" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr className="border-b border-gray-200 h-[50px]">
                        <td colSpan={4} className="px-6 text-center">
                          No support items found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </InternalContainer>
      </div>

      <SupportManagementModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingItem(null); }}
        initialEditItem={editingItem}
      />

      <DeleteSupportModal
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setItemToDelete(null); }}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
        question={itemToDelete?.question}
      />

      <ViewSupportModal
        isOpen={viewModalOpen}
        onClose={() => { setViewModalOpen(false); setViewingItem(null); }}
        item={viewingItem}
      />
    </ExternalContainer>
  );
};

export default Support;
